'use client';

import { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { askAboutLocation, extractLocationUpdates, patchLocationFields } from '@/lib/actions';
import { Bot, Send, Sparkles, Wand2, ArrowLeft, Check, CheckSquare, Square } from 'lucide-react';
import MarkdownRenderer from './MarkdownRenderer';

interface CustomField { label: string; value: string; }

interface Location {
  name: string;
  lore?: string;
  custom_fields?: string;
}

interface Props {
  locationId: string;
  location: Location;
}

type CommitState = 'idle' | 'extracting' | 'reviewing' | 'saving' | 'saved';

interface Proposals {
  lore?: string;
  custom_fields?: CustomField[];
}

export default function LocationBrainstorm({ locationId, location }: Props) {
  const router = useRouter();
  const [messages, setMessages] = useState<{ role: string; content: string }[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [commitState, setCommitState] = useState<CommitState>('idle');
  const [proposals, setProposals] = useState<Proposals>({});
  const [edited, setEdited] = useState<Proposals>({});
  const [checkedFields, setCheckedFields] = useState<Set<string>>(new Set());

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    setMessages(prev => [...prev, { role: 'user', content: input }]);
    setInput('');
    setLoading(true);
    try {
      const response = await askAboutLocation(locationId, input);
      setMessages(prev => [...prev, { role: 'bot', content: response }]);
    } catch {
      setMessages(prev => [...prev, { role: 'bot', content: 'The Oracle is silent.' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleStartCommit = async () => {
    setCommitState('extracting');
    try {
      const result = await extractLocationUpdates(locationId, messages) as Proposals;
      const hasContent = result.lore || (result.custom_fields && result.custom_fields.length > 0);
      if (!hasContent) {
        setMessages(prev => [...prev, {
          role: 'bot',
          content: "Hmm, I couldn't find specific details to save yet. Try asking about history, legends, or what makes this place unique!",
        }]);
        setCommitState('idle');
        return;
      }
      const checked = new Set<string>();
      if (result.lore) checked.add('lore');
      result.custom_fields?.forEach((_, i) => checked.add(`cf_${i}`));
      setProposals(result);
      setEdited({ lore: result.lore, custom_fields: result.custom_fields });
      setCheckedFields(checked);
      setCommitState('reviewing');
    } catch {
      setCommitState('idle');
    }
  };

  const toggleField = (key: string) => setCheckedFields(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const handleConfirm = async () => {
    setCommitState('saving');
    const patches: Parameters<typeof patchLocationFields>[1] = {};
    if (checkedFields.has('lore') && edited.lore) patches.lore = edited.lore;
    const newCF = (edited.custom_fields || []).filter((_, i) => checkedFields.has(`cf_${i}`));
    if (newCF.length > 0) patches.new_custom_fields = newCF;
    await patchLocationFields(locationId, patches);
    setCommitState('saved');
    setTimeout(() => { setCommitState('idle'); router.refresh(); }, 1800);
  };

  const renderChat = () => (
    <>
      <div className="flex-1 overflow-y-auto p-6 space-y-4 text-sm">
        {messages.length === 0 && (
          <p className="text-slate-500 italic text-center mt-12">
            "What hidden secrets lie within this place? Ask me for local legends, weather, or architecture."
          </p>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`p-3 rounded-2xl ${m.role === 'user' ? 'bg-blue-600 ml-8' : 'bg-slate-800 border border-slate-700 mr-8'}`}>
            <MarkdownRenderer content={m.content} />
          </div>
        ))}
        {loading && <div className="animate-pulse bg-slate-800 border border-slate-700 p-3 rounded-2xl w-24 mr-8 h-8" />}
        <div ref={messagesEndRef} />
      </div>

      {messages.length >= 2 && (
        <div className="px-4 pb-2">
          <button
            onClick={handleStartCommit}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 py-2 text-xs font-mono uppercase tracking-widest text-blue-300 hover:text-blue-200 border border-blue-500/20 hover:border-blue-500/40 rounded-xl bg-blue-900/10 hover:bg-blue-900/20 transition-all disabled:opacity-40"
          >
            <Wand2 size={14} /> Apply ideas to {location.name}'s lore
          </button>
        </div>
      )}

      <div className="p-4 border-t border-slate-800 flex gap-2">
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSend()}
          placeholder="Ask about this place..."
          className="flex-1 bg-slate-950 border border-slate-800 rounded-xl px-4 py-2 text-xs outline-none focus:ring-1 focus:ring-blue-500"
        />
        <button onClick={handleSend} disabled={loading} className="p-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-white disabled:opacity-50">
          <Send size={16} />
        </button>
      </div>
    </>
  );

  const renderExtracting = () => (
    <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center p-8">
      <div className="w-12 h-12 rounded-full bg-blue-900 border border-blue-500 flex items-center justify-center animate-pulse">
        <Sparkles size={22} className="text-blue-400" />
      </div>
      <p className="text-slate-300 font-serif italic">Reviewing your brainstorm...</p>
    </div>
  );

  const renderReview = () => (
    <>
      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        <div className="flex items-center gap-2 mb-1">
          <button onClick={() => setCommitState('idle')} className="p-1 text-slate-500 hover:text-slate-300">
            <ArrowLeft size={16} />
          </button>
          <h4 className="text-sm font-mono uppercase tracking-widest text-slate-400">Choose what to save</h4>
        </div>
        <p className="text-xs text-slate-500 italic pb-1">
          Edit anything, uncheck what you don't want, then confirm.
        </p>

        {proposals.lore && (
          <div className={`rounded-xl border transition-colors ${checkedFields.has('lore') ? 'border-slate-700 bg-slate-800/50' : 'border-slate-800 bg-slate-900/30 opacity-50'}`}>
            <button onClick={() => toggleField('lore')} className="w-full flex items-center gap-3 px-4 pt-3 pb-2 text-left">
              {checkedFields.has('lore') ? <CheckSquare size={16} className="shrink-0 text-blue-400" /> : <Square size={16} className="shrink-0 text-slate-600" />}
              <span className="text-xs font-mono uppercase tracking-widest font-bold text-blue-400">History & Lore</span>
            </button>
            {checkedFields.has('lore') && (
              <div className="px-4 pb-3">
                <textarea
                  value={edited.lore ?? ''}
                  onChange={e => setEdited(prev => ({ ...prev, lore: e.target.value }))}
                  rows={5}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500/50 resize-y"
                />
                {location.lore && (
                  <p className="text-xs text-slate-600 italic mt-1">
                    Current: {location.lore.slice(0, 80)}{location.lore.length > 80 ? '…' : ''}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {proposals.custom_fields?.map((cf, i) => {
          const isChecked = checkedFields.has(`cf_${i}`);
          return (
            <div key={i} className={`rounded-xl border transition-colors ${isChecked ? 'border-slate-700 bg-slate-800/50' : 'border-slate-800 bg-slate-900/30 opacity-50'}`}>
              <button onClick={() => toggleField(`cf_${i}`)} className="w-full flex items-center gap-3 px-4 pt-3 pb-2 text-left">
                {isChecked ? <CheckSquare size={16} className="shrink-0 text-blue-400" /> : <Square size={16} className="shrink-0 text-slate-600" />}
                <span className="text-xs font-mono uppercase tracking-widest font-bold text-blue-400">New: {cf.label}</span>
              </button>
              {isChecked && (
                <div className="px-4 pb-3 space-y-1.5">
                  <input
                    value={edited.custom_fields?.[i]?.label ?? cf.label}
                    onChange={e => setEdited(prev => { const cfs = [...(prev.custom_fields ?? proposals.custom_fields ?? [])]; cfs[i] = { ...cfs[i], label: e.target.value }; return { ...prev, custom_fields: cfs }; })}
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-slate-300 focus:outline-none"
                  />
                  <textarea
                    value={edited.custom_fields?.[i]?.value ?? cf.value}
                    onChange={e => setEdited(prev => { const cfs = [...(prev.custom_fields ?? proposals.custom_fields ?? [])]; cfs[i] = { ...cfs[i], value: e.target.value }; return { ...prev, custom_fields: cfs }; })}
                    rows={3}
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none resize-y"
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="p-4 border-t border-slate-800 flex gap-2">
        <button onClick={() => setCommitState('idle')} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-xl text-sm text-slate-400 transition-colors">
          Keep chatting
        </button>
        <button
          onClick={handleConfirm}
          disabled={checkedFields.size === 0}
          className="flex-1 flex items-center justify-center gap-2 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl text-sm font-bold transition-colors"
        >
          <Check size={16} /> Apply to {location.name}
        </button>
      </div>
    </>
  );

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden flex flex-col h-[500px] shadow-2xl">
      <header className="p-4 border-b border-slate-800 bg-slate-900/50 flex items-center gap-3 shrink-0">
        <Sparkles size={18} className="text-blue-400" />
        <h3 className="font-serif font-bold italic text-blue-300">Map the Unknown</h3>
      </header>

      {commitState === 'idle'       && renderChat()}
      {commitState === 'extracting' && renderExtracting()}
      {commitState === 'reviewing'  && renderReview()}
      {commitState === 'saving'     && (
        <div className="flex-1 flex flex-col items-center justify-center gap-4 p-8">
          <div className="w-12 h-12 rounded-full bg-blue-900 border border-blue-500 flex items-center justify-center animate-pulse">
            <Sparkles size={22} className="text-blue-400" />
          </div>
          <p className="text-slate-300 font-serif italic">Saving to {location.name}...</p>
        </div>
      )}
      {commitState === 'saved' && (
        <div className="flex-1 flex flex-col items-center justify-center gap-4 p-8">
          <div className="w-14 h-14 rounded-full bg-emerald-900/60 border border-emerald-500/50 flex items-center justify-center">
            <Check size={24} className="text-emerald-400" />
          </div>
          <p className="text-emerald-300 font-serif italic font-bold">Saved!</p>
        </div>
      )}
    </div>
  );
}
